import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subscription, throwError, Subject } from 'rxjs';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { faList } from '@fortawesome/free-solid-svg-icons/faList';
import { catchError, mergeMap, distinctUntilChanged, map, debounceTime, switchMap, take, skip } from 'rxjs/operators';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { HelperActivityService } from '../../../services/helper/helper-activity/helper-activity.service';
import { ActivityInterface } from '../../../models/interface/activity.interface';
import { TableSortInterface } from '../../../models/interface/table/table_sort.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { StudyInterface } from '../../../models/interface/study/study.interface';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { StudyActionTypes } from '../../../store/study/study.action';
import { UserActionTypes } from '../../../store/user/user.action';
import { UserStore } from '../../../store/user/component-store/user.store';

/**
 * Component:
 * Activity page displaying a list of activities;
 * Can be found: {uri}/activities
 */

@Component({
  selector: 'app-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss'],
  providers: [UserStore]
})
export class ActivityComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  // Table
  public displayedColumns: string[] = ['activity_text', 'performed_by', 'created_at'];
  public dataSource: MatTableDataSource<ActivityInterface>;
  public tableSort: TableSortInterface = {
    sortActivityId: null,
    sortAction: null,
    sortActionId: null,
    sortText: null,
    sortPerformedBy: null,
    sortCreatedAt: null
  };

  // Icons
  public faList = faList;
  public faInfoCircle = faInfoCircle;

  // Filter
  public filter = {
    checkBoxStudy: false,
    checkboxAnswersheet: false,
    searchFilter: '',
    studyFilter: ''
  };
  public keyUp = new Subject<any>();

  // Dropdown - Selected list
  public selectedStudy: StudyInterface;
  public alternative = 'activity.no_studies_available';

  public isLoading$: Observable<boolean>;
  public isCollapse$: Observable<boolean>;
  public showStudies$: Observable<StudyInterface[]>;
  public tableContent$: Observable<ActivityInterface[]>;
  public showDescription$: Observable<boolean>;

  public members: UserInterface[] = [];

  public isCollapseSubject = new BehaviorSubject<boolean>(true);
  public showDescriptionSubject = new BehaviorSubject<boolean>(false);
  private studies: StudyInterface[] = [];

  private myMembers$: Observable<UserInterface[]>;
  private myRegisteredUsers$: Observable<UserInterface[]>;
  private studyActivities$: Observable<ActivityInterface[]>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private showStudiesSubject = new BehaviorSubject<StudyInterface[]>([]);
  private tableContentSubject = new BehaviorSubject<ActivityInterface[]>([]);

  private subscriptions: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    private helperActivityService: HelperActivityService,
    private actions$: Actions,
    private userStore: UserStore,
    private store: Store<{
      myMembers: UserInterface[];
      myRegisteredUsers: UserInterface[];
    }>,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.isCollapse$ = this.isCollapseSubject.asObservable();
    this.showStudies$ = this.showStudiesSubject.asObservable();
    this.tableContent$ = this.tableContentSubject.asObservable();
    this.showDescription$ = this.showDescriptionSubject.asObservable();

    this.subscriptions.push(
      this.keyUp
        .pipe(
          map((event: any) => event.target.value),
          debounceTime(1000),
          distinctUntilChanged(),
          mergeMap((search) => {
            this.applyActivityFilter();
            return of(search);
          }),
        )
        .subscribe()
    );

    this.myMembers$ = this.store.select('myMembers');
    this.myRegisteredUsers$ = this.store.select('myRegisteredUsers');
    this.studyActivities$ = this.userStore.studyActivities$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperActivity() {
    return this.helperActivityService;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.isLoading$.subscribe(() => {
        if (this.paginator) {
          this.paginator.firstPage();
        }
      })
    );

    this.store.dispatch({ type: UserActionTypes.getMyMembersType, payload: {} });
    this.subscriptions.push(
      this.myMembers$.subscribe((result) => {
        this.members = this.helper.setUniqueMembers(result, this.members);
      })
    );

    this.store.dispatch({
      type: UserActionTypes.getUsersRegisteredByEcoachType,
      payload: {},
    });
    this.subscriptions.push(
      this.myRegisteredUsers$.subscribe((result) => {
        this.members = this.helper.setUniqueMembers(result, this.members);
      }),
    );

    this.store.dispatch({
      type: StudyActionTypes.getCollaboratingStudiesType,
      payload: { type: 'study', role: `study.owner,study.ecoachmanager,study.ecoach` },
    });
    this.subscriptions.push(
      this.actions$
        .pipe(
          ofType(StudyActionTypes.getCollaboratingStudiesSuccessType, StudyActionTypes.getCollaboratingStudiesErrorType),
          take(1),
          switchMap((action: any) => {
            if (action.type === StudyActionTypes.getCollaboratingStudiesSuccessType) {
              this.studies = action['response']['body']['data'];
              this.showStudiesSubject.next(this.studies);
              this.selectedStudy = this.studies[0];
              return this.reloadActivityList();
            } else {
              return throwError(() => action);
            }
          })
        )
        .subscribe({
          next: (_result) => this.isLoadingSubject.next(false),
          error: () => this.isLoadingSubject.next(false),
        }),
    );
  }

  // Display Activities of Selected Intervention
  public showSelected(): void {
    this.isLoadingSubject.next(true);
    this.isCollapseSubject.next(true);
    this.filter['checkboxStudy'] = false;
    this.filter['checkboxAnswersheet'] = false;
    this.filter['searchFilter'] = '';
    this.applyActivityFilter();
  }

  public applyActivityFilter(): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadActivityList().subscribe((_result) => {
        this.isLoadingSubject.next(false);
      })
    );
  }

  public updateList(): void {
    this.subscriptions.push(this.reloadActivityList().subscribe());
  }

  // Get study name in dropdown list
  public displaySelect(selected: StudyInterface): string {
    return selected !== undefined ? selected.attributes.name : this.alternative;
  }

  public reloadActivityList(sort?: Sort): Observable<boolean> {
    this.userStore.getActivitiesStudy({ studyId: this.selectedStudy.id, include: 'questionnaires' });
    return this.studyActivities$.pipe(
      skip(1),
      take(1),
      switchMap((activities) => {
        const filterArray = this.filterAndSort(activities);
        const currentPageSize = this.paginator ? this.paginator.pageSize : 20;
        if (sort) {
          this.dataSource = this.sortData(sort, filterArray, this.tableContentSubject, this.members, this.tableSort);
        } else {
          this.dataSource = new MatTableDataSource<ActivityInterface>(filterArray.slice(0, currentPageSize));
          this.tableContentSubject.next(filterArray);
        }
        if (this.paginator) {
          this.paginator.length = filterArray.length;
          this.paginator.firstPage();
        }
        return of(true);
      }),
      catchError((_error) => {
        this.tableContentSubject.next([]);
        return of(true);
      })
    );
  }

  public onPageChangeActivity(event: PageEvent): void {
    const filterArray: ActivityInterface[] = this.filterAndSort(this.tableContentSubject.value);
    const startIndex = event.pageIndex * event.pageSize;
    const endIndex = startIndex + event.pageSize > filterArray.length ? filterArray.length : startIndex + event.pageSize;
    if (this.paginator) {
      this.paginator.length = filterArray.length;
    }
    this.dataSource = this.sortData(this.sort, filterArray.slice(), this.tableContentSubject, this.members, this.tableSort);
    this.dataSource = new MatTableDataSource<ActivityInterface>(filterArray.slice(startIndex, endIndex));
    this.dataSource.sort = this.sort;
  }

  public onChangeSortTable(sort: Sort): void {
    this.reloadActivityList(sort).subscribe();
  }

  public onKeyFilter(): void {
    const filterResults: StudyInterface[] = this.studies.filter((study) =>
      study.attributes.name.toLowerCase().includes(this.filter['studyFilter'].toLowerCase()),
    );
    this.showStudiesSubject.next(filterResults.length !== 0 ? filterResults : [this.selectedStudy]);
  }

  public resetFilter(): void {
    this.filter['studyFilter'] = '';
    this.showStudiesSubject.next(this.studies);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private sortData(
    sort: Sort,
    data: ActivityInterface[],
    tableContentSubject: BehaviorSubject<ActivityInterface[]>,
    members: UserInterface[],
    tableSort: TableSortInterface,
  ): MatTableDataSource<ActivityInterface> {
    const isDesc = sort.direction === 'desc';
    switch (sort.active) {
      case 'activity_text':
        tableContentSubject.next(
          data.sort((a: ActivityInterface, b: ActivityInterface) => {
            const behavior = new BehaviorSubject<number>(null);
            forkJoin([this.helperActivity.getActivityText(a), this.helperActivity.getActivityText(b)]).subscribe((activities: string[]) => {
              const act1 = this.helperActivity.getSubmittedLessonNameOfActivity(a, activities[0]);
              const act2 = this.helperActivity.getSubmittedLessonNameOfActivity(b, activities[1]);
              behavior.next(isDesc ? act2.localeCompare(act1) : act1.localeCompare(act2));
            });
            return behavior.value;
          })
        );
        break;
      case 'performed_by':
        this.helperActivityService.toggleSortPerformedBy(data, isDesc, tableContentSubject, members, tableSort);
        break;
      case 'created_at':
        this.helperActivityService.toggleSortCreated(data, isDesc, tableContentSubject, tableSort);
        break;
    }
    const currentPageSize = this.paginator ? this.paginator.pageSize : 20;
    return new MatTableDataSource<ActivityInterface>(tableContentSubject.value.slice(0, currentPageSize));
  }

  private filterAndSort(activities: ActivityInterface[]): ActivityInterface[] {
    this.tableContentSubject.next(activities);
    const subscribedToStudy = this.filter['checkboxStudy'];
    const answersheetSubmitted = this.filter['checkboxAnswersheet'];
    let filterArray: ActivityInterface[];
    if (!subscribedToStudy && !answersheetSubmitted) {
      filterArray = activities;
    } else {
      filterArray = [];
      if (subscribedToStudy) {
        activities.forEach((activity) => {
          if (activity.attributes.action === 'STUDY_SUBSCRIBED') {
            filterArray.push(activity);
          }
        });
      }
      if (answersheetSubmitted) {
        activities.forEach((activity) => {
          if (activity.attributes.action === 'ANSWERSHEET_SUBMITTED') {
            filterArray.push(activity);
          }
        });
      }
    }
    return this.helper.filterActivitiesByUser(
      filterArray,
      this.filter['searchFilter'],
      this.members,
    );
  }
}
